import {
  Add as AddIcon,
  Star as AdvancedIcon,
  School as BeginnerIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  TrendingUp as IntermediateIcon,
  Psychology as PsychologyIcon,
} from "@mui/icons-material";
import {
  Alert,
  alpha,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  DataGrid,
  GridActionsCellItem,
  GridColDef,
  GridPaginationModel,
} from "@mui/x-data-grid";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useModules } from "../../hooks/useRedux";
import { Module } from "../../store/slices/types";

const Modules: React.FC = () => {
  const navigate = useNavigate();
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10,
  });
  const { modules, loading, error, deleteModule, pagination } = useModules(
    paginationModel.page + 1,
    paginationModel.pageSize
  );
  const [activeFilter, setActiveFilter] = useState<string | null>(null);

  // Filter modules based on active filter
  const filteredModules = activeFilter
    ? modules.filter((module) => {
        if (activeFilter === "beginner")
          return module.difficulty === "beginner";
        if (activeFilter === "intermediate")
          return module.difficulty === "intermediate";
        if (activeFilter === "advanced")
          return module.difficulty === "advanced";
        if (activeFilter === "with-programs")
          return module.programId && module.programId !== "";
        if (activeFilter === "without-programs")
          return !module.programId || module.programId === "";
        return true;
      })
    : modules;

  // Calculate statistics
  const totalModules = modules.length;
  const beginnerModules = modules.filter(
    (m) => m.difficulty === "beginner"
  ).length;
  const intermediateModules = modules.filter(
    (m) => m.difficulty === "intermediate"
  ).length;
  const advancedModules = modules.filter(
    (m) => m.difficulty === "advanced"
  ).length;

  const handleFilterClick = (filter: string | null) => {
    setActiveFilter(filter);
  };

  if (error) {
    return (
      <Box>
        <Alert severity='error' sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  const getDifficultyIcon = (difficulty: string) => {
    switch (difficulty) {
      case "beginner":
        return <BeginnerIcon />;
      case "intermediate":
        return <IntermediateIcon />;
      case "advanced":
        return <AdvancedIcon />;
      default:
        return <BeginnerIcon />;
    }
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case "beginner":
        return "success";
      case "intermediate":
        return "warning";
      case "advanced":
        return "error";
      default:
        return "default";
    }
  };

  const formatDate = (date: number | string | Date | undefined) => {
    if (!date) return "N/A";
    const dateObj = typeof date === 'number' ? new Date(date) : new Date(date);
    if (isNaN(dateObj.getTime())) return "N/A";
    return dateObj.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const columns: GridColDef[] = [
    {
      field: "title",
      headerName: "Title",
      flex: 1.5,
      minWidth: 200,
      filterable: true,
      renderCell: (params) => (
        <Typography variant='body2' fontWeight='600'>
          {params.value}
        </Typography>
      ),
    },
    {
      field: "difficulty",
      headerName: "Difficulty",
      flex: 0.6,
      minWidth: 100,
      filterable: true,
      renderCell: (params) => (
        <Chip
          icon={getDifficultyIcon(params.value)}
          label={params.value}
          size='small'
          color={getDifficultyColor(params.value) as any}
          variant='filled'
        />
      ),
    },
    {
      field: "weekNumber",
      headerName: "Week",
      flex: 0.4,
      minWidth: 80,
      renderCell: (params) => (
        <Chip
          label={`Week ${params.value}`}
          size='small'
          color='secondary'
          variant='outlined'
        />
      ),
    },
    {
      field: "order",
      headerName: "Order",
      flex: 0.4,
      minWidth: 80,
      renderCell: (params) => (
        <Typography variant='body2' fontWeight='600'>
          #{params.value}
        </Typography>
      ),
    },
    {
      field: "programId",
      headerName: "Program",
      flex: 0.8,
      minWidth: 120,
      renderCell: (params) => {
        // Use programDetails from backend (populated by backend)
        const programDetails = params.row.programDetails;
        if (programDetails?.title) {
          return (
            <Chip
              label={programDetails.title}
              size='small'
              color='primary'
              variant='filled'
              sx={{ maxWidth: "100%" }}
            />
          );
        }
        return (
          <Chip
            label='No Program'
            size='small'
            color='default'
            variant='outlined'
            sx={{ maxWidth: "100%" }}
          />
        );
      },
    },
    {
      field: "tags",
      headerName: "Tags",
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <Box>
          {params.value?.slice(0, 2).map((tag: string) => (
            <Chip
              key={tag}
              label={tag}
              size='small'
              sx={{ mr: 0.5, mb: 0.5 }}
              color='secondary'
              variant='outlined'
            />
          ))}
          {params.value?.length > 2 && (
            <Chip
              label={`+${params.value.length - 2}`}
              size='small'
              sx={{ mr: 0.5, mb: 0.5 }}
              color='default'
              variant='outlined'
            />
          )}
        </Box>
      ),
    },
    {
      field: "createdAt",
      headerName: "Created At",
      flex: 0.8,
      minWidth: 150,
      filterable: true,
      renderCell: (params) => (
        <Typography variant='body2'>{formatDate(params.value)}</Typography>
      ),
    },
    {
      field: "updatedAt",
      headerName: "Last Updated",
      flex: 0.8,
      minWidth: 150,
      filterable: true,
      renderCell: (params) => (
        <Typography variant='body2'>{formatDate(params.value)}</Typography>
      ),
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      flex: 0.3,
      minWidth: 80,
      getActions: (params) => [
        <GridActionsCellItem
          icon={
            <Tooltip title='Edit Module'>
              <EditIcon />
            </Tooltip>
          }
          label='Edit'
          onClick={() => handleEdit(params.row)}
        />,
        <GridActionsCellItem
          icon={
            <Tooltip title='Delete Module'>
              <DeleteIcon />
            </Tooltip>
          }
          label='Delete'
          onClick={() => handleDelete(params.row.id)}
        />,
      ],
    },
  ];

  const handleAdd = () => {
    navigate("/modules/add");
  };

  const handleEdit = (module: Module) => {
    const id = module._id || module.id;
    if (id) {
      navigate(`/modules/edit/${id}`);
    } else {
      toast.error("Invalid module ID");
    }
  };

  const handleDelete = async (id: string) => {
    const module = modules.find((m) => m.id === id);
    const moduleTitle = module?.title || "Module";

    if (
      window.confirm(
        `Are you sure you want to delete "${moduleTitle}"? This action cannot be undone.`
      )
    ) {
      try {
        await deleteModule(id);
        toast.success("module is deleted");
      } catch (error) {
        console.error("Failed to delete module:", error);
        toast.error(
          `❌ Failed to delete module: ${
            error instanceof Error ? error.message : "Unknown error"
          }`
        );
      }
    }
  };

  return (
    <Box>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Box>
            <Typography variant='h4' fontWeight='700' gutterBottom>
              Module Management
            </Typography>
            <Typography variant='body1' color='text.secondary'>
              Create and manage wellness content modules
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              variant='contained'
              startIcon={<AddIcon />}
              onClick={handleAdd}
              sx={{
                borderRadius: 2,
                px: 3,
                py: 1.5,
                textTransform: "none",
                fontWeight: 600,
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)",
                },
              }}
            >
              Create Module
            </Button>
          </Box>
        </Box>

        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                background:
                  activeFilter === null
                    ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                    : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: "white",
                borderRadius: 2,
                cursor: "pointer",
                transition: "all 0.3s ease",
                transform: activeFilter === null ? "scale(1.02)" : "scale(1)",
                boxShadow:
                  activeFilter === null
                    ? "0 8px 25px rgba(102, 126, 234, 0.3)"
                    : "0 2px 8px rgba(0,0,0,0.1)",
                "&:hover": {
                  transform: "scale(1.02)",
                  boxShadow: "0 8px 25px rgba(102, 126, 234, 0.3)",
                },
              }}
              onClick={() => handleFilterClick(null)}
            >
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box>
                    <Typography variant='h4' fontWeight='bold'>
                      {totalModules}
                    </Typography>
                    <Typography variant='body2' sx={{ opacity: 0.9 }}>
                      Total Modules
                    </Typography>
                  </Box>
                  <PsychologyIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                borderRadius: 2,
                cursor: "pointer",
                transition: "all 0.3s ease",
                transform:
                  activeFilter === "beginner" ? "scale(1.02)" : "scale(1)",
                boxShadow:
                  activeFilter === "beginner"
                    ? "0 8px 25px rgba(76, 175, 80, 0.3)"
                    : "0 2px 8px rgba(0,0,0,0.1)",
                border:
                  activeFilter === "beginner"
                    ? "2px solid #4caf50"
                    : "2px solid transparent",
                "&:hover": {
                  transform: "scale(1.02)",
                  boxShadow: "0 8px 25px rgba(76, 175, 80, 0.3)",
                },
              }}
              onClick={() => handleFilterClick("beginner")}
            >
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box>
                    <Typography
                      variant='h4'
                      fontWeight='bold'
                      color='success.main'
                    >
                      {beginnerModules}
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                      Beginner
                    </Typography>
                  </Box>
                  <BeginnerIcon sx={{ fontSize: 40, color: "success.main" }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                borderRadius: 2,
                cursor: "pointer",
                transition: "all 0.3s ease",
                transform:
                  activeFilter === "intermediate" ? "scale(1.02)" : "scale(1)",
                boxShadow:
                  activeFilter === "intermediate"
                    ? "0 8px 25px rgba(255, 152, 0, 0.3)"
                    : "0 2px 8px rgba(0,0,0,0.1)",
                border:
                  activeFilter === "intermediate"
                    ? "2px solid #ff9800"
                    : "2px solid transparent",
                "&:hover": {
                  transform: "scale(1.02)",
                  boxShadow: "0 8px 25px rgba(255, 152, 0, 0.3)",
                },
              }}
              onClick={() => handleFilterClick("intermediate")}
            >
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box>
                    <Typography
                      variant='h4'
                      fontWeight='bold'
                      color='warning.main'
                    >
                      {intermediateModules}
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                      Intermediate
                    </Typography>
                  </Box>
                  <IntermediateIcon
                    sx={{ fontSize: 40, color: "warning.main" }}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                borderRadius: 2,
                cursor: "pointer",
                transition: "all 0.3s ease",
                transform:
                  activeFilter === "advanced" ? "scale(1.02)" : "scale(1)",
                boxShadow:
                  activeFilter === "advanced"
                    ? "0 8px 25px rgba(244, 67, 54, 0.3)"
                    : "0 2px 8px rgba(0,0,0,0.1)",
                border:
                  activeFilter === "advanced"
                    ? "2px solid #f44336"
                    : "2px solid transparent",
                "&:hover": {
                  transform: "scale(1.02)",
                  boxShadow: "0 8px 25px rgba(244, 67, 54, 0.3)",
                },
              }}
              onClick={() => handleFilterClick("advanced")}
            >
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box>
                    <Typography
                      variant='h4'
                      fontWeight='bold'
                      color='error.main'
                    >
                      {advancedModules}
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                      Advanced
                    </Typography>
                  </Box>
                  <AdvancedIcon sx={{ fontSize: 40, color: "error.main" }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Filter Indicator */}
      {activeFilter && (
        <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant='body2' color='text.secondary'>
            Showing {activeFilter.replace("-", " ")} modules:
          </Typography>
          <Chip
            label={activeFilter.replace("-", " ")}
            color={
              activeFilter === "beginner"
                ? "success"
                : activeFilter === "intermediate"
                ? "warning"
                : activeFilter === "advanced"
                ? "error"
                : activeFilter === "with-programs"
                ? "info"
                : activeFilter === "without-programs"
                ? "warning"
                : "default"
            }
            size='small'
            onDelete={() => handleFilterClick(null)}
          />
        </Box>
      )}

      {/* Data Table */}
      <Paper sx={{ borderRadius: 2, overflow: "hidden" }}>
        <DataGrid
          rows={filteredModules}
          columns={columns}
          getRowId={(row) => row._id || row.id || String(Math.random())}
          paginationMode='server'
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50, 100]}
          rowCount={pagination?.total || 0}
          loading={loading}
          autoHeight
          disableColumnMenu={false}
          disableColumnFilter={false}
          disableColumnSelector={false}
          filterModel={undefined}
          onFilterModelChange={() => {}}
          sx={{
            border: "none",
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: alpha("#667eea", 0.1),
              borderBottom: `2px solid ${alpha("#667eea", 0.2)}`,
            },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: alpha("#667eea", 0.05),
            },
            "& .MuiDataGrid-cell:focus": {
              outline: "none",
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              fontWeight: 600,
            },
            "& .MuiDataGrid-filterForm": {
              display: "flex",
              alignItems: "center",
              gap: 1,
            },
          }}
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 10 },
            },
          }}
        />
      </Paper>
    </Box>
  );
};

export default Modules;
